import * as React from "react";

import { Button, styled, Typography } from "@mui/material";
import { AlertProps, default as MUIAlert } from "@mui/material/Alert";

import { UserContext } from "context/usercontext";

const Alert = styled(MUIAlert)(({ theme }) => ({
  ...theme.typography.body2,
  padding: "10px",
  marginBottom: "5px",
}));

interface NotificationsProps {
  workflowCountUnknown: boolean;
  userDisabled: boolean;
  workflowUsernameUnavailable: boolean;
  keypairUnavailable: boolean;
  missingNotifArn: boolean;
  invalidOrg: boolean;
  missingIngressRules: boolean;
  noAvailableInstances: boolean;
}

const SimpleNotification = (message: React.ReactNode, props?: AlertProps) => {
  return <Alert {...props}>{message}</Alert>;
};

const WorkflowStatusUnknown = () => {
  return SimpleNotification(
    "Trouble pulling workflow information. Please try refreshing the page. If the problem persists, please contact support.",
    {
      severity: "warning",
    }
  );
};

const UserNameAlert = () => {
  return SimpleNotification(
    "You do not have an email address or preferred workflow username set for your account. One of these must be set to start a workflow. Please contact an administrator for help.",
    {
      severity: "warning",
    }
  );
};

const UserStatusAlert = () => {
  return SimpleNotification(
    "Welcome to Metworx! Congratulations! You have been successfully authenticated. However, for security purposes you will next need to contact your Organization Administrator to be granted further access.",
    {
      icon: false,
      severity: "success",
    }
  );
};

const OrgConfigAlert = () => {
  return SimpleNotification(
    "Your organization is not properly configured to launch workflows. Please contact your organization's administrator.",
    {
      severity: "warning",
    }
  );
};

const KeyPairAlert = () => {
  return SimpleNotification(
    "No AWS key names set. If you know your key name, click here to set. Otherwise, contact administrator.",
    {
      severity: "warning",
    }
  );
};

const MissingIngressRulesAlert = () => {
  return SimpleNotification(
    "Ingress Rules have not been defined for your private network, please contact your organization's administrator.",
    {
      severity: "warning",
    }
  );
};

const NoAvailableInstancesAlert = () => {
  return SimpleNotification("No Instances are available for use, please contact your organization's administrator.", {
    severity: "warning",
  });
};

export const Notifications: React.FC<NotificationsProps> = ({
  workflowCountUnknown,
  userDisabled,
  workflowUsernameUnavailable,
  keypairUnavailable,
  missingNotifArn,
  invalidOrg,
  missingIngressRules,
  noAvailableInstances,
}) => {
  const user = React.useContext(UserContext);

  const MissingNotifArnAlert = () => {
    if (user.is_org_admin) {
      return (
        <Alert severity="error">
          <Typography sx={{ fontWeight: 600 }}>Missing Infrastructure</Typography>
          <Typography variant="body2">
            Your organization is missing important infrastructure. Please open a support ticket to get this resolved.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            href="https://metworx.atlassian.net/servicedesk/customer/portal/6"
            target="_blank"
            sx={{ marginTop: "10px" }}
          >
            Contact Support
          </Button>
        </Alert>
      );
    }

    return (
      <Alert severity="error">
        <Typography sx={{ fontWeight: 600 }}>Missing Infrastructure</Typography>
        <Typography variant="body2">
          Your organization is missing important infrastructure. Please contact your organization administrator.
        </Typography>
      </Alert>
    );
  };

  return (
    <>
      {workflowCountUnknown && <WorkflowStatusUnknown />}
      {userDisabled && <UserStatusAlert />}
      {invalidOrg && <OrgConfigAlert />}
      {workflowUsernameUnavailable && <UserNameAlert />}
      {keypairUnavailable && <KeyPairAlert />}
      {missingNotifArn && <MissingNotifArnAlert />}
      {missingIngressRules && <MissingIngressRulesAlert />}
      {noAvailableInstances && <NoAvailableInstancesAlert />}
    </>
  );
};
